import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  Trophy,
  Medal,
  Crown,
  Users,
  Eye,
  EyeOff,
  Loader2,
  AlertTriangle
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { BeltIndicator } from '../shared/BeltBadge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Rank styling
const getRankStyle = (rank) => {
  switch (rank) {
    case 1:
      return {
        bg: 'bg-gradient-to-r from-yellow-100 to-amber-100 dark:from-yellow-900/40 dark:to-amber-900/40',
        border: 'border-yellow-400 dark:border-yellow-600',
        icon: Crown,
        iconColor: 'text-yellow-500',
        text: 'text-yellow-800 dark:text-yellow-300'
      };
    case 2:
      return {
        bg: 'bg-gradient-to-r from-gray-100 to-slate-100 dark:from-gray-700 dark:to-slate-800',
        border: 'border-gray-400 dark:border-gray-500',
        icon: Medal,
        iconColor: 'text-gray-400',
        text: 'text-gray-700 dark:text-gray-300'
      };
    case 3:
      return {
        bg: 'bg-gradient-to-r from-orange-100 to-amber-50 dark:from-orange-900/40 dark:to-amber-900/30',
        border: 'border-orange-400 dark:border-orange-600',
        icon: Medal,
        iconColor: 'text-orange-600',
        text: 'text-orange-800 dark:text-orange-300'
      };
    default:
      return {
        bg: 'bg-white dark:bg-gray-800',
        border: 'border-gray-200 dark:border-gray-700',
        icon: null,
        iconColor: '',
        text: 'text-gray-700 dark:text-gray-300'
      };
  }
};

// ============================================
// Leaderboard Entry Row
// ============================================
const LeaderboardEntry = ({ entry, isCurrentUser }) => {
  const rankStyle = getRankStyle(entry.rank);
  const RankIcon = rankStyle.icon;

  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg border ${rankStyle.border} ${rankStyle.bg} ${
      isCurrentUser ? 'ring-2 ring-purple-400 ring-offset-2' : ''
    }`}>
      {/* Rank */}
      <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${rankStyle.text}`}>
        {RankIcon ? (
          <RankIcon className={`h-6 w-6 ${rankStyle.iconColor}`} />
        ) : (
          <span>#{entry.rank}</span>
        )}
      </div>

      {/* Student Info */}
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className={`font-semibold ${isCurrentUser ? 'text-purple-700' : ''}`}>
            {entry.student_name}
          </span>
          {isCurrentUser && (
            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
              You
            </span>
          )}
        </div>
        {entry.current_belt && (
          <BeltIndicator belt={entry.current_belt} size="xs" />
        )}
      </div>

      {/* Score */}
      <div className="text-right">
        <p className={`text-xl font-bold ${rankStyle.text}`}>
          {entry.score.toLocaleString()}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">points</p>
      </div>
    </div>
  );
};

// ============================================
// Opt-In Toggle
// ============================================
const OptInToggle = ({ optedIn, onChange, loading }) => {
  return (
    <Card className={optedIn ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {optedIn ? (
              <Eye className="h-5 w-5 text-purple-600" />
            ) : (
              <EyeOff className="h-5 w-5 text-gray-400" />
            )}
            <div>
              <p className="font-medium">
                {optedIn ? 'Visible on Leaderboard' : 'Hidden from Leaderboard'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {optedIn
                  ? 'Other opted-in students can see your rank'
                  : 'Your scores are private'}
              </p>
            </div>
          </div>

          <Button
            variant={optedIn ? 'memberOutline' : 'memberPrimary'}
            size="sm"
            onClick={onChange}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              optedIn ? 'Opt Out' : 'Join Leaderboard'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// Main Leaderboard View Component
// ============================================
const LeaderboardView = ({ studentId, token }) => {
  const [leaderboards, setLeaderboards] = useState([]);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [currentUserRank, setCurrentUserRank] = useState(null);
  const [optedIn, setOptedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [togglingOptIn, setTogglingOptIn] = useState(false);
  const [timeframe, setTimeframe] = useState('weekly');

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // Fetch leaderboard configs and opt-in status
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch leaderboard configurations
        const leaderboardsRes = await fetch(
          `${API_BASE}/api/customer/leaderboards`,
          { headers: getAuthHeaders() }
        );
        if (leaderboardsRes.ok) {
          const data = await leaderboardsRes.json();
          setLeaderboards(data.leaderboards || []);
          if (data.leaderboards?.length > 0) {
            setSelectedLeaderboard(data.leaderboards[0]);
          }
          setOptedIn(data.user_opted_in || false);
        }
      } catch (error) {
        console.error('Failed to fetch leaderboards:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAuthHeaders]);

  // Fetch leaderboard entries when selection changes
  useEffect(() => {
    if (!selectedLeaderboard) return;

    const fetchEntries = async () => {
      setLoadingEntries(true);
      try {
        const response = await fetch(
          `${API_BASE}/api/customer/leaderboards/${selectedLeaderboard.id}?timeframe=${timeframe}`,
          { headers: getAuthHeaders() }
        );
        if (response.ok) {
          const data = await response.json();
          setEntries(data.entries || []);
          setCurrentUserRank(data.current_user_rank || null);
        }
      } catch (error) {
        console.error('Failed to fetch entries:', error);
      } finally {
        setLoadingEntries(false);
      }
    };

    fetchEntries();
  }, [selectedLeaderboard, timeframe, getAuthHeaders]);

  const handleOptInToggle = async () => {
    setTogglingOptIn(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/leaderboard-opt-in`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ leaderboard_opt_in: !optedIn })
        }
      );

      if (response.ok) {
        setOptedIn(!optedIn);
        // Refresh entries if opting in
        if (!optedIn && selectedLeaderboard) {
          const entriesRes = await fetch(
            `${API_BASE}/api/customer/leaderboards/${selectedLeaderboard.id}?timeframe=${timeframe}`,
            { headers: getAuthHeaders() }
          );
          if (entriesRes.ok) {
            const data = await entriesRes.json();
            setEntries(data.entries || []);
            setCurrentUserRank(data.current_user_rank || null);
          }
        }
      }
    } catch (error) {
      console.error('Failed to toggle opt-in:', error);
    } finally {
      setTogglingOptIn(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Show message if no leaderboards are configured
  if (leaderboards.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium mb-2">No Leaderboards Available</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Leaderboards haven't been set up yet. Check back soon!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-purple-600" />
          Leaderboard
        </h2>
      </div>

      {/* Opt-In Toggle */}
      <OptInToggle
        optedIn={optedIn}
        onChange={handleOptInToggle}
        loading={togglingOptIn}
      />

      {/* Privacy Note */}
      <div className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-2 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
        <AlertTriangle className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
        <span>
          Only students who opt-in can see and be seen on the leaderboard.
          Your attendance, training hours, and start date are never shown.
        </span>
      </div>

      {/* Timeframe Selector */}
      <div className="flex gap-2">
        {['weekly', 'monthly', 'all_time'].map(tf => (
          <Button
            key={tf}
            variant={timeframe === tf ? 'memberPrimary' : 'memberOutline'}
            size="sm"
            onClick={() => setTimeframe(tf)}
          >
            {tf === 'weekly' && 'This Week'}
            {tf === 'monthly' && 'This Month'}
            {tf === 'all_time' && 'All Time'}
          </Button>
        ))}
      </div>

      {/* Leaderboard Content */}
      {!optedIn ? (
        <Card>
          <CardContent className="p-8 text-center">
            <EyeOff className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium mb-2">Join the Leaderboard</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Opt in to see how you rank against other students and compete for the top spots!
            </p>
            <Button
              onClick={handleOptInToggle}
              disabled={togglingOptIn}
              variant="memberPrimary"
            >
              {togglingOptIn ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trophy className="h-4 w-4 mr-2" />
              )}
              Join Leaderboard
            </Button>
          </CardContent>
        </Card>
      ) : loadingEntries ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
        </div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium mb-2">No Rankings Yet</h3>
            <p className="text-gray-600 dark:text-gray-400">
              Be the first to earn points and claim the top spot!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Top 3 Podium */}
          {entries.length >= 3 && (
            <div className="grid grid-cols-3 gap-2 mb-4">
              {/* Second Place */}
              <div className="pt-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center border border-gray-200 dark:border-gray-700">
                  <Medal className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                  <p className="font-semibold text-sm truncate text-gray-800 dark:text-gray-200">{entries[1]?.student_name}</p>
                  <p className="text-lg font-bold text-gray-600 dark:text-gray-300">{entries[1]?.score.toLocaleString()}</p>
                </div>
              </div>

              {/* First Place */}
              <div>
                <div className="bg-gradient-to-b from-yellow-50 to-amber-100 dark:from-yellow-900/40 dark:to-amber-900/40 rounded-lg p-3 text-center border-2 border-yellow-400 dark:border-yellow-600">
                  <Crown className="h-8 w-8 text-yellow-500 mx-auto mb-1" />
                  <p className="font-semibold text-sm truncate text-gray-800 dark:text-gray-200">{entries[0]?.student_name}</p>
                  <p className="text-xl font-bold text-yellow-700 dark:text-yellow-400">{entries[0]?.score.toLocaleString()}</p>
                </div>
              </div>

              {/* Third Place */}
              <div className="pt-8">
                <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-3 text-center border border-orange-300 dark:border-orange-700">
                  <Medal className="h-5 w-5 text-orange-600 mx-auto mb-1" />
                  <p className="font-semibold text-sm truncate text-gray-800 dark:text-gray-200">{entries[2]?.student_name}</p>
                  <p className="text-lg font-bold text-orange-600 dark:text-orange-400">{entries[2]?.score.toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}

          {/* Full Rankings */}
          <div className="space-y-2">
            {entries.map((entry) => (
              <LeaderboardEntry
                key={entry.student_id}
                entry={entry}
                isCurrentUser={entry.student_id === studentId}
              />
            ))}
          </div>

          {/* Current User Rank (if not in top list) */}
          {currentUserRank && !entries.find(e => e.student_id === studentId) && (
            <div className="pt-4 border-t dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Your Ranking</p>
              <LeaderboardEntry
                entry={currentUserRank}
                isCurrentUser={true}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeaderboardView;
